import './OkulSepetleriPage.scss'

import {CaretDownFilled, CaretDownOutlined, SearchOutlined} from '@ant-design/icons'
import {Button, Col, DatePicker, Input, Row, Select, Switch, Table} from 'antd'
import dayjs, {Dayjs} from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import {saveAs} from 'file-saver'
import {RangeValue} from 'rc-picker/lib/interface'
import React, {useContext, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import * as XLSX from 'xlsx'

import AppContext from '../../../../context/AppContext'
import {useOkulSepetleriQuery} from '../../../../gql/GeneratedGraphqlTypes'
import Spinner from '../../../spinner/Spinner'
import {ReactComponent as AktarIcon} from '../okul-siparis-page/icons/aktar.svg'

dayjs.extend(isBetween)

const {RangePicker} = DatePicker
const {Search} = Input
const {Option} = Select
const OkulSepetleriPage: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState<string>('')
    const {okul, siparisYetki} = useContext(AppContext)
    const [searchValue, setSearchValue] = useState<string>('')
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined)
    const [pageSize, setPageSize] = useState(10);
    // todo:tarih okul başlangıç tarihi olacak
    const [dateRange, setDateRange] = useState<RangeValue<Dayjs>>([
        dayjs(okul?.okul_acilis_tarih ? okul.okul_acilis_tarih : '2024-06-01'),
        dayjs(),
    ])

    const navigate = useNavigate()
    const {data, loading, error, refetch} = useOkulSepetleriQuery({
        variables: {
            sube_id: okul?.id,
            kategori_secimi: parseInt(selectedCategory!) ?? null,
            bas_tarih: dateRange?.[0]?.format('YYYY-MM-DD') ?? '2024-06-01',
            bit_tarih: dateRange?.[1]?.format('YYYY-MM-DD') ?? '2025-06-01',
            arama: searchValue || null, // 2 karakterden küçükse arama yapılmaz
        },
        fetchPolicy: 'network-only',
    })

    // Filtreleme değiştikçe veriyi tekrar çek
    useEffect(() => {
        refetch()
    }, [searchValue, dateRange, refetch])

    if (loading) {
        return <Spinner showSpinner={loading} color="red"/>
    }

    if (error) {
        console.error(error)
        navigate('/')
        return null
    }
    const handleFilterChange = (filterType: 'durum' | 'odeme' | 'kargo' | 'kategori', value: string | undefined) => {
        if (value === null) {
            if (filterType === 'kategori') setSelectedCategory(value);
        } else {
            if (filterType === 'kategori') setSelectedCategory(value);
        }
    };


    const handleDateChange = (values: RangeValue<Dayjs>) => {
        if (values && values[0] && values[1]) {
            setDateRange(values)
        }
    }
    const handleSearch = () => {
        setSearchValue(searchTerm)
    }

    const filteredData = data?.okulSepetleri?.sepetler?.filter(Boolean) ?? []
    const totalItems = filteredData.length;
    const displayedItems = selectedRowKeys.length;

    const kategoriOptions = [
        <Option key="tum" value={null}>Tümü</Option>, // 🔹 Tümünü ekledik
        ...(data?.okulSepetleri?.uyeKategorileri?.map((kargo) => (
            <Option key={kargo?.id} value={kargo?.id}>
                {kargo?.isim}
            </Option>
        )) || [])
    ];
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            sorter: (a: any, b: any) => a.id - b.id,
            defaultSortOrder: 'descend' as 'descend' | 'ascend' | undefined,
        },
        {title: 'Okul', dataIndex: 'okul', key: 'okul'},
        {title: 'User', dataIndex: 'uye', key: 'uye'},
        {title: 'Sınıf İsmi', dataIndex: 'sinif', key: 'sinif'},
        {
            title: 'Tarih',
            dataIndex: 'tarih',
            key: 'tarih',
            sorter: (a: any, b: any) => dayjs(a.tarih).valueOf() - dayjs(b.tarih).valueOf(),
        },
        {
            title: 'Değişim',
            dataIndex: 'degisim',
            render: (_: string, record: any) => {
                return record.degisim ? (
                    <div>
                        <Switch checked={true} disabled={true}/>
                    </div>
                ) : (
                    <div>
                        <Switch checked={false} disabled={true}/>
                    </div>
                )
            },
            key: 'degisim',
        },
        {
            title: '',
            dataIndex: 'siparis_detay',
            key: 'siparis_detay',
            render: (_: any, record: any) => (
                <Button onClick={() => navigate(`${record.id}`)} className="okul-yonetim-admin-detail-button">
                    Detay
                </Button>
            ),
        },
    ]
    const exportAllToExcel = () => {
        const worksheetData: any[] = []

        const headers = ['Sepet Id','Okul', 'Üye', 'Sınıf', 'Başlangıç Tarihi', 'Tarih', 'Değişim']
        worksheetData.push(headers)

        data?.okulSepetleri?.sepetler.forEach((sepetler: any) => {
            const transformedData = [
                sepetler.id || '',
                sepetler.okul || '',
                sepetler.uye || '',
                sepetler.sinif || '',
                sepetler.baslangic_tarih || '',
                sepetler.tarih || '',
                sepetler.degisim ? 'Evet' : 'Hayır',
            ]
            worksheetData.push(transformedData)
        })

        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

        const colWidths = worksheetData[0].map((_: string, colIndex: number) =>
            Math.max(...worksheetData.map((row) => (row[colIndex] ? row[colIndex].toString().length : 10))),
        )
        worksheet['!cols'] = colWidths.map((width: number) => ({wch: width}))

        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'TümVeriler')
        const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'})
        saveAs(new Blob([excelBuffer]), `sepetler.xlsx`)
    }
    return (
        <div className="okul-siparis-page">
            <div className="okul-sepeti-admin-header">
                <h1>Okul Sepetleri</h1>
                {siparisYetki &&
                    <Button onClick={exportAllToExcel} className="export-button">
                        <AktarIcon/> Dışa Aktar{' '}
                    </Button>
                }
            </div>
            {siparisYetki ? (
                    <>
                        <Row gutter={8} className="filter-section">
                            <Col span={6}>
                                <span className="okul-order-page-filter-title">Müşteri, Sınıf veya Sipariş Ara</span>

                                <Search
                                    placeholder="Müşteri, Sınıf veya Sipariş Ara"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onSearch={handleSearch}
                                    allowClear
                                    enterButton={
                                        <div className="search-suffix-div">
                                            Ara <SearchOutlined/>{' '}
                                        </div>
                                    }
                                />
                            </Col>
                            <Col span={6}>
                                <span className="okul-order-page-filter-title">Kategori</span>

                                <Select
                                    suffixIcon={<CaretDownFilled style={{fontSize: 20}}/>}
                                    placeholder="Seçiniz"
                                    value={selectedCategory}
                                    onChange={(value) => handleFilterChange('kategori', value)}
                                    allowClear
                                >
                                    {kategoriOptions}
                                </Select>
                            </Col>
                            <Col span={6}>
                                <span className="okul-order-page-filter-title">Tarhi Aralığı</span>

                                <RangePicker
                                    suffixIcon={
                                        <>
                                            Değiştir <CaretDownOutlined/>{' '}
                                        </>
                                    }
                                    value={dateRange}
                                    onChange={handleDateChange}
                                    format="YYYY-MM-DD"
                                />
                            </Col>
                        </Row>

                        <Table
                            dataSource={filteredData as object[]}
                            columns={columns}
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                total: totalItems,
                                showTotal: (total) => (
                                    <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                <span style={{marginLeft: '10px'}}> {/* Buton ile metin arasına boşluk koyuyoruz */}
                    Seçilen: {displayedItems} / Toplam: {total}
               </span>
                                    </div>
                                ),
                                onChange: (page, size) => {
                                    setCurrentPage(page);
                                    setPageSize(size);
                                },
                            }}
                            rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-light' : 'table-row-dark')}
                            loading={loading}
                            rowKey="id"
                            rowSelection={{
                                type: 'checkbox',
                                ...Selection,
                                onChange: (keys) => setSelectedRowKeys(keys as string[]),
                            }}
                            className="okul-yonetim-admin-table"
                        />
                    </>
                )
                :
                <p>Sepetleri görme yetkiniz bulunmamaktadır. İlgili okul temsilcinizden talep edebilirsiniz.</p>
            }

        </div>
    )
}

export default OkulSepetleriPage
